import { useState } from "react";
import { motion } from "framer-motion";
import { Flag, Users, CheckCircle2, Send, Loader2, GraduationCap } from "lucide-react";
import SectionHead from "./SectionHead";
import API, { formatApiErrorDetail } from "../../lib/api";
import { fadeUp, stagger, sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

const EMPTY = { nome: "", istituto: "", ruolo: "", email: "", telefono: "", num_studenti: "", messaggio: "" };

export default function Scuole() {
  const { t } = useLang();
  const c = t.scuole;
  const [modalita, setModalita] = useState("team_in_pista");
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim() || !form.istituto.trim() || !form.email.trim()) {
      setError(c.error);
      return;
    }
    setStatus("loading");
    try {
      await API.post("/adesioni", {
        ...form,
        ruolo: form.ruolo || c.roleOptions[0],
        num_studenti: form.num_studenti ? Number(form.num_studenti) : null,
      });
      setStatus("success");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  const reset = () => {
    setForm(EMPTY);
    setStatus("idle");
  };

  return (
    <section id="scuole" className={`relative bg-vg-black ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="03" kicker={c.kicker} title={c.title} en={c.en} />

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid gap-6 lg:grid-cols-2">
          <motion.article
            variants={fadeUp}
            onClick={() => setModalita("team_in_pista")}
            className={`group cursor-pointer border p-8 transition-all duration-300 sm:p-10 ${
              modalita === "team_in_pista" ? "border-vg-green bg-vg-green/5 shadow-[0_0_40px_rgba(16,185,129,0.12)]" : "border-white/10 bg-vg-card hover:border-vg-green/40"
            }`}
            data-testid="opzione-team-in-pista"
          >
            <div className="flex items-center justify-between">
              <Flag className={`h-9 w-9 ${modalita === "team_in_pista" ? "text-vg-green" : "text-vg-muted"}`} />
              <span className="font-display text-5xl font-bold uppercase text-outline">A</span>
            </div>
            <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-wide text-vg-white">{c.optionA.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-vg-muted sm:text-base">{c.optionA.desc}</p>
            <span className={`mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] ${modalita === "team_in_pista" ? "text-vg-green" : "text-vg-muted"}`}>
              {modalita === "team_in_pista" && <CheckCircle2 className="h-4 w-4" />}
              {c.optionA.tag}
            </span>
          </motion.article>

          <motion.article
            variants={fadeUp}
            onClick={() => setModalita("delegazione")}
            className={`group cursor-pointer border p-8 transition-all duration-300 sm:p-10 ${
              modalita === "delegazione" ? "border-vg-green bg-vg-green/5 shadow-[0_0_40px_rgba(16,185,129,0.12)]" : "border-white/10 bg-vg-card hover:border-vg-green/40"
            }`}
            data-testid="opzione-delegazione"
          >
            <div className="flex items-center justify-between">
              <Users className={`h-9 w-9 ${modalita === "delegazione" ? "text-vg-green" : "text-vg-muted"}`} />
              <span className="font-display text-5xl font-bold uppercase text-outline">B</span>
            </div>
            <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-wide text-vg-white">{c.optionB.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-vg-muted sm:text-base">{c.optionB.desc}</p>
            <span className={`mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] ${modalita === "delegazione" ? "text-vg-green" : "text-vg-muted"}`}>
              {modalita === "delegazione" && <CheckCircle2 className="h-4 w-4" />}
              {c.optionB.tag}
            </span>
          </motion.article>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 grid overflow-hidden border border-white/10 lg:grid-cols-12"
          data-testid="adesione-form-section"
        >
          <div className="relative hidden lg:col-span-4 lg:block">
            <div className="absolute inset-0 bg-vg-green/10" aria-hidden="true" />
            <div className="relative flex h-full flex-col justify-between p-10">
              <GraduationCap className="h-12 w-12 text-vg-green" />
              <div>
                <h3 className="font-display text-4xl font-bold uppercase leading-tight tracking-wide text-vg-white">{c.formTitle}</h3>
                <p className="mt-4 text-sm leading-relaxed text-vg-muted">{c.formIntro}</p>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{c.formTag}</p>
              </div>
              <div className="clip-frame relative mt-8 h-48 shrink-0 overflow-hidden border border-white/10">
                <img src="/img/ipsia.jpg" alt="Monoposto elettrica di un istituto partecipante" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-vg-black/80 to-transparent" aria-hidden="true" />
                <p className="absolute bottom-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-vg-white">{c.imgCaption}</p>
              </div>
            </div>
          </div>

          <div className="bg-vg-card p-8 sm:p-10 lg:col-span-8">
            {status === "success" ? (
              <div className="flex h-full flex-col items-center justify-center py-16 text-center" data-testid="adesione-success">
                <CheckCircle2 className="h-16 w-16 text-vg-green" />
                <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-wide text-vg-white">{c.successTitle}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-vg-muted">
                  {c.success(form.nome.split(" ")[0] || "", form.istituto, form.email)}
                </p>
                <button
                  onClick={reset}
                  data-testid="adesione-reset-button"
                  className="mt-8 rounded-full border border-vg-white/25 px-6 py-3 font-display text-sm font-semibold uppercase tracking-wider text-vg-white transition-colors hover:border-vg-green hover:text-vg-green"
                >
                  {c.reset}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid="adesione-form">
                <div className="sm:col-span-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-vg-green">{c.modeLabel}</span>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {c.modes.map((opt) => (
                      <label
                        key={opt.v}
                        className={`flex cursor-pointer items-center gap-3 border px-4 py-3 text-sm transition-colors ${
                          modalita === opt.v ? "border-vg-green bg-vg-green/10 text-vg-white" : "border-white/10 bg-vg-black text-vg-muted hover:border-vg-green/40"
                        }`}
                      >
                        <input
                          type="radio"
                          name="modalita"
                          value={opt.v}
                          checked={modalita === opt.v}
                          onChange={() => setModalita(opt.v)}
                          data-testid={`adesione-modalita-${opt.v}`}
                          className="h-4 w-4 accent-[#10B981]"
                        />
                        {opt.l}
                      </label>
                    ))}
                  </div>
                </div>

                {[
                  { k: "nome", type: "text", tid: "adesione-nome", req: true },
                  { k: "istituto", type: "text", tid: "adesione-istituto", req: true },
                  { k: "email", type: "email", tid: "adesione-email", req: true },
                  { k: "telefono", type: "tel", tid: "adesione-telefono", req: false },
                ].map((f) => (
                  <div key={f.k}>
                    <label htmlFor={f.tid} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                      {c.fields[f.k]}
                    </label>
                    <input
                      id={f.tid}
                      type={f.type}
                      required={f.req}
                      value={form[f.k]}
                      onChange={set(f.k)}
                      data-testid={f.tid}
                      className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white placeholder-vg-muted/50 outline-none transition-colors focus:border-vg-green"
                    />
                  </div>
                ))}

                <div>
                  <label htmlFor="adesione-ruolo" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                    {c.roleLabel}
                  </label>
                  <select
                    id="adesione-ruolo"
                    value={form.ruolo || c.roleOptions[0]}
                    onChange={set("ruolo")}
                    data-testid="adesione-ruolo"
                    className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white outline-none transition-colors focus:border-vg-green"
                  >
                    {c.roleOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="adesione-num-studenti" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                    {c.fields.num_studenti}
                  </label>
                  <input
                    id="adesione-num-studenti"
                    type="number"
                    min="1"
                    value={form.num_studenti}
                    onChange={set("num_studenti")}
                    data-testid="adesione-num-studenti"
                    className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white placeholder-vg-muted/50 outline-none transition-colors focus:border-vg-green"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="adesione-messaggio" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">
                    {c.fields.messaggio}
                  </label>
                  <textarea
                    id="adesione-messaggio"
                    rows="3"
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    data-testid="adesione-messaggio"
                    placeholder={c.msgPlaceholder}
                    className="w-full border border-white/10 bg-vg-black px-4 py-3 text-sm text-vg-white placeholder-vg-muted/50 outline-none transition-colors focus:border-vg-green"
                  />
                </div>

                {error && (
                  <p className="rounded border border-vg-red/40 bg-vg-red/10 px-4 py-3 text-sm text-vg-red sm:col-span-2" data-testid="adesione-error">
                    {error}
                  </p>
                )}

                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="adesione-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-vg-green px-8 py-4 font-display text-lg font-semibold uppercase tracking-wider text-vg-black transition-all duration-200 hover:shadow-[0_0_36px_rgba(16,185,129,0.55)] disabled:opacity-60 sm:w-auto"
                  >
                    {status === "loading" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
                    {status === "loading" ? c.sending : c.submit}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
